/**
 * Public waiver sign page — `/waiver/sign/:token` on the tenant host.
 *
 * NO customer session. The `wst_` token in the URL is the only credential:
 *   GET  /api/customer/waiver/by-link/:token         → waiver html + version + name
 *   POST /api/customer/waiver/by-link/:token/accept  → typed-name signature
 *
 * States: loading → sign form | already signed | success | expired (410) |
 * superseded (409) | invalid (404) | no waiver (422). Expired + superseded
 * both tell the customer to ask the kennel for a fresh link — staff mint one
 * from the customer page.
 */
import { type CSSProperties, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BrandedShell } from '../components/BrandedShell';
import { CustomerApiError, customerApi } from '../customer_api';
import { RenderWaiverHtml } from './WaiverAcceptHtml';

interface LinkWaiverResponse {
  html: string;
  version: number;
  accountName: string;
  customerFirstName: string;
  expiresAt: string;
  alreadySigned: { acceptedAt: string; signatureText: string } | null;
}

interface AcceptResponse {
  id: string;
  version: number;
  acceptedAt: string;
  alreadySigned: boolean;
}

type PageState =
  | { kind: 'loading' }
  | { kind: 'ready'; waiver: LinkWaiverResponse }
  | { kind: 'signed'; acceptedAt: string; accountName: string | null }
  | { kind: 'expired' }
  | { kind: 'superseded' }
  | { kind: 'invalid' }
  | { kind: 'no_waiver' }
  | { kind: 'error'; message: string };

function stateFromError(err: unknown): PageState {
  if (err instanceof CustomerApiError) {
    const code = (err.body as { error?: string } | null)?.error;
    if (err.status === 410 || code === 'link_expired') return { kind: 'expired' };
    if (err.status === 409 || code === 'waiver_superseded') return { kind: 'superseded' };
    if (err.status === 404 || code === 'invalid_link') return { kind: 'invalid' };
    if (err.status === 422 || code === 'no_waiver_configured') return { kind: 'no_waiver' };
    if (err.status === 429) {
      return { kind: 'error', message: 'Too many attempts — please wait a minute and try again.' };
    }
    return { kind: 'error', message: `Could not load the waiver (${err.status}).` };
  }
  return { kind: 'error', message: 'Could not load the waiver. Please try again.' };
}

function formatAcceptedAt(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return new Intl.DateTimeFormat('en-GB', { dateStyle: 'long', timeStyle: 'short' }).format(d);
}

export function WaiverSignByLink() {
  const { token = '' } = useParams<{ token: string }>();
  const [state, setState] = useState<PageState>({ kind: 'loading' });
  const [signature, setSignature] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setState({ kind: 'invalid' });
      return;
    }
    customerApi<LinkWaiverResponse>(`/api/customer/waiver/by-link/${encodeURIComponent(token)}`)
      .then((waiver) => {
        if (cancelled) return;
        if (waiver.alreadySigned) {
          setState({
            kind: 'signed',
            acceptedAt: waiver.alreadySigned.acceptedAt,
            accountName: waiver.accountName,
          });
        } else {
          setState({ kind: 'ready', waiver });
        }
      })
      .catch((err) => {
        if (!cancelled) setState(stateFromError(err));
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (state.kind !== 'ready') return;
    const trimmed = signature.trim();
    if (!trimmed || !agreed) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      const res = await customerApi<AcceptResponse>(
        `/api/customer/waiver/by-link/${encodeURIComponent(token)}/accept`,
        { method: 'POST', body: JSON.stringify({ signatureText: trimmed }) },
      );
      setState({
        kind: 'signed',
        acceptedAt: res.acceptedAt,
        accountName: state.waiver.accountName,
      });
    } catch (err) {
      const next = stateFromError(err);
      if (next.kind === 'expired' || next.kind === 'superseded' || next.kind === 'invalid') {
        setState(next);
      } else {
        const rateLimited = next.kind === 'error' && next.message.startsWith('Too many');
        setSubmitError(
          rateLimited && next.kind === 'error'
            ? next.message
            : 'Could not record your signature. Please try again.',
        );
      }
    } finally {
      setSubmitting(false);
    }
  }

  const wrapperStyle: CSSProperties = {
    minHeight: '100dvh',
    background: 'var(--background, oklch(0.98 0.005 80))',
    color: 'var(--foreground, oklch(0.19 0.012 75))',
  };
  const mutedStyle: CSSProperties = {
    color: 'var(--muted-foreground, oklch(0.45 0.005 80))',
    fontSize: '0.9rem',
  };
  const panelStyle: CSSProperties = {
    marginTop: '1.5rem',
    padding: '1.25rem 1.5rem',
    borderRadius: '12px',
    background: 'var(--surface, white)',
    border: '1px solid var(--border, oklch(0.92 0.005 80))',
    fontSize: '0.95rem',
    lineHeight: 1.55,
  };

  return (
    <BrandedShell>
      <div style={wrapperStyle}>
        <div style={{ maxWidth: '720px', margin: '0 auto', padding: '3rem 1.25rem 5rem' }}>
          {state.kind === 'loading' && (
            <p style={mutedStyle} aria-busy="true">
              Loading your waiver…
            </p>
          )}

          {state.kind === 'invalid' && (
            <section aria-label="Link not valid">
              <h1 style={{ fontSize: '1.5rem', fontWeight: 600 }}>This link isn't valid</h1>
              <p style={mutedStyle}>
                The signing link is incomplete or was not issued for this site. Please ask us to
                send you a new one.
              </p>
            </section>
          )}

          {state.kind === 'expired' && (
            <section aria-label="Link expired">
              <h1 style={{ fontSize: '1.5rem', fontWeight: 600 }}>This link has expired</h1>
              <p style={mutedStyle}>
                Signing links last 7 days. Please ask us to send you a fresh link and you can sign
                in a couple of minutes.
              </p>
            </section>
          )}

          {state.kind === 'superseded' && (
            <section aria-label="Waiver updated">
              <h1 style={{ fontSize: '1.5rem', fontWeight: 600 }}>Our waiver has changed</h1>
              <p style={mutedStyle}>
                The waiver was updated after this link was sent, so it can no longer be signed from
                here. Please ask us for a fresh link to the current version.
              </p>
            </section>
          )}

          {state.kind === 'no_waiver' && (
            <section aria-label="No waiver">
              <h1 style={{ fontSize: '1.5rem', fontWeight: 600 }}>Nothing to sign right now</h1>
              <p style={mutedStyle}>
                There is currently no waiver to sign. You can close this page.
              </p>
            </section>
          )}

          {state.kind === 'error' && (
            <p role="alert" style={{ color: 'var(--destructive, oklch(0.55 0.18 30))' }}>
              {state.message}
            </p>
          )}

          {state.kind === 'signed' && (
            <section aria-label="Waiver signed" style={panelStyle}>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                Thank you — your waiver is signed
              </h1>
              <p style={mutedStyle}>
                {state.accountName ? `${state.accountName} has ` : 'We have '}a record of your
                signature from {formatAcceptedAt(state.acceptedAt)}. You can close this page.
              </p>
            </section>
          )}

          {state.kind === 'ready' && (
            <>
              <h1 style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                {state.waiver.customerFirstName
                  ? `Hi ${state.waiver.customerFirstName}, please sign our waiver`
                  : 'Please review and sign our waiver'}
              </h1>
              <p style={mutedStyle}>
                Read the {state.waiver.accountName} waiver below and type your full name to sign. No
                account or sign-in is needed.
              </p>

              <RenderWaiverHtml html={state.waiver.html} />

              <form
                onSubmit={handleSubmit}
                style={{
                  marginTop: '1.5rem',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                }}
              >
                <label
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.4rem',
                    fontSize: '0.9rem',
                  }}
                >
                  Type your full name
                  <input
                    type="text"
                    value={signature}
                    onChange={(e) => setSignature(e.target.value)}
                    placeholder="Jane Doe"
                    aria-label="Signature text"
                    autoComplete="name"
                    style={{
                      padding: '0.55rem 0.75rem',
                      borderRadius: '8px',
                      border: '1px solid var(--border, oklch(0.915 0.005 80))',
                      background: 'var(--surface, white)',
                      fontSize: '0.95rem',
                      fontFamily: 'cursive',
                    }}
                  />
                </label>

                <label
                  style={{
                    display: 'flex',
                    gap: '0.5rem',
                    alignItems: 'flex-start',
                    fontSize: '0.9rem',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    aria-label="I have read and agree"
                    style={{ marginTop: '0.2rem' }}
                  />
                  <span>
                    I have read and agree to the terms above (version {state.waiver.version}).
                  </span>
                </label>

                {submitError !== null && (
                  <p role="alert" style={{ color: 'var(--destructive, oklch(0.55 0.18 30))' }}>
                    {submitError}
                  </p>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={submitting || !agreed || signature.trim().length === 0}
                    style={{
                      padding: '0.7rem 1.4rem',
                      background: 'var(--primary, oklch(0.4 0.15 254))',
                      color: 'white',
                      borderRadius: '999px',
                      border: 'none',
                      fontSize: '0.95rem',
                      fontWeight: 500,
                      cursor: submitting ? 'not-allowed' : 'pointer',
                      opacity: submitting || !agreed || signature.trim().length === 0 ? 0.6 : 1,
                    }}
                  >
                    {submitting ? 'Recording…' : 'I accept'}
                  </button>
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </BrandedShell>
  );
}
